import { chartColour } from './palette';

export interface DonutSegment {
  key: string;
  label: string;
  value: number;
  colour?: string;
}

interface DonutChartProps {
  data: readonly DonutSegment[];
  caption: string;
  /** Die grosse Zahl in der Mitte -- meist die Summe. */
  total: string;
  /** Was die Zahl in der Mitte bedeutet. */
  totalLabel: string;
}

/** Radius und Strichstaerke im Koordinatensystem des ``viewBox``. */
const RADIUS = 60;
const STROKE = 24;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * Ein Ring mit einem Segment je Kategorie.
 *
 * **Ein Ring und keine Torte.** Die Mitte traegt die Summe -- die Zahl, die
 * sonst darueber stuende und den Blick ein zweites Mal kosten wuerde. Und
 * Segmente lassen sich am Umfang besser vergleichen als Kreisausschnitte an
 * der Flaeche; die Flaeche einer Torte ueberschaetzt jeder.
 *
 * Gezeichnet ueber ``stroke-dasharray`` und einen wandernden ``dashoffset``.
 * Das ist der kurze Weg: keine Bogenberechnung, kein Sonderfall bei einem
 * Segment ueber 180 Grad -- der Grund, aus dem handgeschriebene Tortenstuecke
 * so oft an genau dieser Stelle brechen.
 *
 * **Die Legende ist Teil des Diagramms und nicht sein Anhang.** Wer Farben
 * nicht unterscheiden kann, liest die Liste; die Farbe ist dann eine
 * Verzierung und keine Information.
 */
export function DonutChart({ data, caption, total, totalLabel }: DonutChartProps) {
  const sum = data.reduce((value, segment) => value + segment.value, 0);

  let offset = 0;
  const arcs = data.map((segment, index) => {
    const share = sum > 0 ? segment.value / sum : 0;
    const arc = {
      key: segment.key,
      colour: segment.colour ?? chartColour(index),
      length: share * CIRCUMFERENCE,
      offset,
    };
    offset += arc.length;
    return arc;
  });

  return (
    <div className="chart-donut">
      <div className="chart-donut__figure">
        <svg viewBox="0 0 160 160" role="img" aria-label={caption} className="chart-donut__svg">
          {/* Die Bahn darunter: Bei wenig Daten sieht der Ring sonst wie ein
              abgebrochener Strich aus. */}
          <circle
            cx="80"
            cy="80"
            r={RADIUS}
            fill="none"
            stroke="var(--chart-track)"
            strokeWidth={STROKE}
          />
          {arcs.map((arc) => (
            <circle
              key={arc.key}
              cx="80"
              cy="80"
              r={RADIUS}
              fill="none"
              stroke={arc.colour}
              strokeWidth={STROKE}
              strokeDasharray={`${String(arc.length)} ${String(CIRCUMFERENCE - arc.length)}`}
              strokeDashoffset={String(-arc.offset)}
              // Bei zwoelf Uhr beginnen und im Uhrzeigersinn laufen -- so
              // liest es jeder, der schon einmal eine Uhr gesehen hat.
              transform="rotate(-90 80 80)"
            />
          ))}
        </svg>
        <div className="chart-donut__centre">
          <strong className="chart-donut__total">{total}</strong>
          <span className="chart-donut__total-label">{totalLabel}</span>
        </div>
      </div>

      <ul className="chart-donut__legend">
        {data.map((segment, index) => (
          <li key={segment.key} className="chart-donut__legend-row">
            <span
              className="chart-donut__swatch"
              style={{ background: segment.colour ?? chartColour(index) }}
              aria-hidden="true"
            />
            <span className="chart-donut__legend-label">{segment.label}</span>
            <strong className="chart-donut__legend-value">{segment.value}</strong>
          </li>
        ))}
      </ul>
    </div>
  );
}
